/**
 * Boat profile + chart settings, kept ON the device (localStorage) so they
 * survive reloads — the one place the user's own-boat figures live, shared by
 * every feature that needs them rather than being re-entered per feature.
 *
 * Fields (room for more — just add to BoatSettings + DEFAULTS and the loader
 * merges old saved data with the new defaults):
 *   - lengthM        overall length, m (stored; for display / own-ship scale)
 *   - draughtM       draught, m — the SINGLE source of truth for the depth-under-
 *                    keel alarm (the Alarms sheet reads/writes this same value)
 *   - airDraughtM    air draught, m — height of the highest fixed point above the
 *                    water; the bridge "clearance now" fit check compares it
 *   - cruiseSpeedKn  cruising / planning speed, kn — the ETA/route speed default
 *                    when there's no SOG (boat stopped) or for passage planning
 *   - safetyDepthM   the depth at/above which the user wants to see hazards; at
 *                    low zoom, wrecks/rocks DEEPER than this drop out of the
 *                    declutter (combined with the relative-hazard rule)
 *   - shadingShallowM / shadingDeepM  the two depth-area shading transitions
 *                    (Planning's ECDIS shallow/safety/deep contour idea): areas
 *                    shoaler than shallow draw shallow-blue, between draw mid,
 *                    deeper than deep draw deep — re-binned live when changed
 *   - haptics        whether the Web Vibration buzz is on (App & Account → Haptics,
 *                    default on); the haptics helper is told whenever this flips
 */
import { create } from 'zustand';
import { DEFAULT_POLAR, type BoatPolar } from '@/passage/polar';
import type { DepthShadingMode } from '@/map/style/layers';
import { setHapticsEnabled } from '@/ui/haptics';
import { setAlarmSoundEnabled } from '@/ui/alarmSound';

export interface BoatSettings {
  lengthM: number | null;
  draughtM: number;
  airDraughtM: number | null;
  cruiseSpeedKn: number;
  /**
   * How the cruising/planning speed above is meant: 'sog' (speed over the ground,
   * tide already baked in — ETAs are the flat distance / speed) or 'stw' (speed
   * through the water, so route ETAs fold the tidal stream into each leg). Default
   * 'sog' keeps the old flat behaviour for anyone who doesn't set it. See PlanSpeed
   * in routing/route.ts. */
  planSpeedRef: 'sog' | 'stw';
  safetyDepthM: number;
  shadingShallowM: number;
  shadingDeepM: number;
  /** How much depth colouring the chart lays down: 'off' (flat, no tints),
   *  'safety' (a single two-tone split at the shallow contour) or 'graduated'
   *  (the full shallow/mid/deep ramp). See setDepthShadingMode in the layers. */
  depthShadingMode: DepthShadingMode;
  /** Use a sailing polar for auto-routing (tacks upwind); off = motor at cruise. */
  polarEnabled: boolean;
  polar: BoatPolar;
  /** Haptic feedback on (Web Vibration API). Default on; no-op where unsupported. */
  haptics: boolean;
  /** Audible alarm siren on. Default on; no-op where the WebAudio API is absent. */
  alarmSound: boolean;
  /**
   * Glass transparency, 0..1. Higher = more see-through (more "liquid glass");
   * lower = more solid. Drives the alpha of the menu/sheet glass surfaces live.
   * Clamped when applied so text never washes out over a busy chart.
   */
  glassTransparency: number;
  /**
   * Own-ship course/speed (COG) and heading vectors: how many minutes ahead the
   * vector line reaches. Mirrors the AIS target vector control; 6 is the usual
   * ECDIS default. A longer time draws a longer line, so speed shows up sooner.
   */
  ownVectorMin: number;
  /**
   * Which symbol draws the boat: the Ardent arrowhead (the brand mark, and the
   * default), the plain triangle, or a hull silhouette that looks like the kind
   * of boat we actually sail (fb-msmb1hm6-5). A skipper looking at their own
   * boat all day should get to choose what it looks like.
   */
  ownshipIcon: 'ardent' | 'triangle' | 'sail' | 'motor' | 'power';
  /**
   * Colour for the own-ship ground vector (the COG/SOG arrow). Null means the
   * palette accent, which recolours with day/dusk/night; a fixed colour stays
   * put across palettes because that is the point of choosing one
   * (fb-msmb2bmg-7).
   */
  ownVectorColour: string | null;
  /**
   * Wind colour thresholds, knots (fb-msmaz0fj-3). What counts as a green day
   * depends on the boat: a dinghy sailor wants green ending at 10, a heavy
   * cruiser is happy to 15. These slide the whole colour ladder: green starts
   * at windGreenFromKn, red at windRedFromKn, and every other boundary scales
   * proportionally between them. Defaults are the Beaufort boundaries the
   * ladder was drawn on. One pair of numbers, used by the chart arrows, the
   * wash, the legend, and the forecast table alike, so every view keeps
   * telling the same colour story.
   */
  windGreenFromKn: number;
  windRedFromKn: number;
  /**
   * SOG display damping, seconds (fb-msn0gnz8-2). Phone GNSS speed jumps about
   * from fix to fix, and a readout flicking 4.8-5.6-4.9 is noise dressed as
   * information. The damping is an exponential average with this time
   * constant, applied to the DEVICE feed only (the simulator is already
   * clean). Zero is off; the raw value still drives the spoof/teleport checks
   * either way, because a safety check must see the jump the display smooths.
   */
  sogDampingSec: number;
  /** Maximum beam, metres. With length, this is what lets us draw the hull. */
  beamM: number | null;
  /**
   * The GNSS antenna's distance AFT of the bow along the centreline, metres.
   *
   * The fix is the ANTENNA's position, not the boat's, and on a yacht with the
   * aerial at the stern rail that is most of a boat-length from the bow. With
   * this set, the hull draws in its real place and the anchor drops where it
   * actually leaves the bow roller rather than several metres aft of it.
   */
  gnssFromBowM: number | null;
  /** Antenna's offset from the centreline, metres, positive to starboard. */
  gnssFromCentreM: number;
  /** Night-mode screen brightness, 0..1 (1 = full bright, lower dims the whole
   *  screen via a veil so nothing glares on a dark passage). */
  nightBrightness: number;
  /** Hold a screen wake lock so the phone doesn't sleep mid-pilotage. Off by
   *  default (it costs battery). */
  keepScreenAwake: boolean;
  /**
   * Whether the first-run walkthrough (the coach-mark tour) has already been
   * shown. False out of the box so a brand-new install gets the tour once; set
   * true when it's finished or skipped. Preserved across a settings reset, since
   * resetting the boat figures shouldn't re-nag with the tour. See Walkthrough.tsx.
   */
  seenWalkthrough: boolean;
}

export const DEFAULT_SETTINGS: BoatSettings = {
  lengthM: null,
  draughtM: 1.5,
  airDraughtM: null,
  cruiseSpeedKn: 5,
  planSpeedRef: 'sog',
  safetyDepthM: 10,
  shadingShallowM: 5,
  shadingDeepM: 10,
  depthShadingMode: 'graduated',
  polarEnabled: false,
  polar: DEFAULT_POLAR,
  haptics: true,
  alarmSound: true,
  // Glassy but readable over the chart. 0.6 maps to ~0.78 surface alpha with the
  // backdrop blur (see glassAlpha in ui/glass.ts); the slider still goes clearer.
  glassTransparency: 0.6,
  ownVectorMin: 6,
  ownshipIcon: 'ardent',
  ownVectorColour: null,
  windGreenFromKn: 11,
  windRedFromKn: 34,
  sogDampingSec: 5,
  beamM: null,
  gnssFromBowM: null,
  gnssFromCentreM: 0,
  nightBrightness: 0.75,
  keepScreenAwake: true,
  seenWalkthrough: false,
};

const KEY = 'acp.settings.v1';

function loadPersisted(): BoatSettings {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (!raw) return { ...DEFAULT_SETTINGS };
    const d = JSON.parse(raw) as Partial<BoatSettings>;
    // Merge over defaults so a saved profile from an older build picks up any
    // newly-added field rather than coming back undefined.
    return { ...DEFAULT_SETTINGS, ...d };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function persist(s: BoatSettings): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / private-mode / SSR */
  }
}

interface SettingsState extends BoatSettings {
  /** Patch one or more fields and persist. */
  set: (patch: Partial<BoatSettings>) => void;
  /** Restore the shipped defaults. */
  reset: () => void;
}

const initial = loadPersisted();
// Bring the haptics helper in line with the persisted preference at start-up, so
// a buzz fired before the user ever opens settings already respects their choice.
setHapticsEnabled(initial.haptics);
// Same for the audible alarm siren, so a safety alarm that fires before the user
// ever opens settings already respects their choice.
setAlarmSoundEnabled(initial.alarmSound);

export const useSettingsStore = create<SettingsState>((set, get) => ({
  ...initial,
  set: (patch) => {
    const next: BoatSettings = {
      lengthM: get().lengthM,
      draughtM: get().draughtM,
      airDraughtM: get().airDraughtM,
      cruiseSpeedKn: get().cruiseSpeedKn,
      planSpeedRef: get().planSpeedRef,
      safetyDepthM: get().safetyDepthM,
      shadingShallowM: get().shadingShallowM,
      shadingDeepM: get().shadingDeepM,
      depthShadingMode: get().depthShadingMode,
      polarEnabled: get().polarEnabled,
      polar: get().polar,
      haptics: get().haptics,
      alarmSound: get().alarmSound,
      glassTransparency: get().glassTransparency,
      ownVectorMin: get().ownVectorMin,
      ownshipIcon: get().ownshipIcon,
      ownVectorColour: get().ownVectorColour,
      windGreenFromKn: get().windGreenFromKn,
      windRedFromKn: get().windRedFromKn,
      sogDampingSec: get().sogDampingSec,
      beamM: get().beamM,
      gnssFromBowM: get().gnssFromBowM,
      gnssFromCentreM: get().gnssFromCentreM,
      nightBrightness: get().nightBrightness,
      keepScreenAwake: get().keepScreenAwake,
      seenWalkthrough: get().seenWalkthrough,
      ...patch,
    };
    persist(next);
    if (patch.haptics !== undefined) setHapticsEnabled(next.haptics);
    if (patch.alarmSound !== undefined) setAlarmSoundEnabled(next.alarmSound);
    set(next);
  },
  reset: () => {
    // Keep the "tour already seen" flag through a reset — a user resetting their
    // boat figures and chart preferences shouldn't be shown the walkthrough again.
    const next: BoatSettings = { ...DEFAULT_SETTINGS, seenWalkthrough: get().seenWalkthrough };
    persist(next);
    setHapticsEnabled(next.haptics);
    setAlarmSoundEnabled(next.alarmSound);
    set({ ...next });
  },
}));
